import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import FormHelperText from '@mui/material/FormHelperText';
import { useTheme } from '@mui/material/styles';
import { CKEditor } from 'ckeditor4-react';
import { InputHelperText, Labeled } from 'react-admin';
import { useController, useFormContext } from 'react-hook-form';

import { EditorConfig } from './EditorConfig';
import { ShortConfig } from './ShortConfig';

const MlStyles = [
    { name: 'Primary link', element: 'a', attributes: { class: 'ml-link' } },
    { name: 'Primary button link', element: 'a', attributes: { class: 'ml-button-primary' } },
    { name: 'Secondary button link', element: 'a', attributes: { class: 'ml-button-secondary' } },
    { name: 'Table', element: 'table', attributes: { class: 'ml-table-alt-rows' } },
    { name: 'Table odd row', element: 'tr', attributes: { class: 'odd' } },
    { name: 'Table even row', element: 'tr', attributes: { class: 'even' } },
    { name: 'Table caption', element: 'caption', attributes: { class: 'ml-table-title' } },
];

const RichTextInput = (props) => {
    const { defValue, short, source, required } = props;
    const { control } = useFormContext();
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';

    const {
        field: { name, onChange, onBlur, onFocus, value },
        fieldState: { error, isTouched: touched },
    } = useController({
        name: source,
        control,
        defaultValue: defValue || '',
    });

    const [editorInstance, setEditorInstance] = useState(null);

    const inputWithRequire = {
        field: {
            name, onChange, onBlur, onFocus, value,
        },
        meta: { error, touched },
        isRequired: Boolean(required),
    };

    const handleBeforeLoad = useCallback((CKEDITOR) => {
        if (!CKEDITOR.stylesSet.registered.mlStyles) {
            CKEDITOR.stylesSet.add('mlStyles', MlStyles);
        }

        if (!CKEDITOR.plugins.registered.scriptLoader) {
            CKEDITOR.plugins.addExternal('scriptLoader', '/lib/ckeditor/plugins/scriptLoader/');
        }

        if (!CKEDITOR.plugins.registered.layoutmanager) {
            CKEDITOR.plugins.addExternal('layoutmanager', '/lib/ckeditor/plugins/layoutmanager/');
        }

        if (!CKEDITOR.plugins.registered.basewidget) {
            CKEDITOR.plugins.addExternal('basewidget', '/lib/ckeditor/plugins/basewidget/');
        }

        if (!CKEDITOR.plugins.registered.table) {
            CKEDITOR.plugins.addExternal('table', '/lib/ckeditor/plugins/table/');
        }

        if (!CKEDITOR.plugins.registered.tabletools) {
            CKEDITOR.plugins.addExternal('tabletools', '/lib/ckeditor/plugins/tabletools/');
        }

        if (!CKEDITOR.plugins.registered.dialog) {
            CKEDITOR.plugins.addExternal('dialog', '/lib/ckeditor/plugins/dialog/');
        }

        if (!CKEDITOR.plugins.registered.bootstrapTabs) {
            CKEDITOR.plugins.addExternal('bootstrapTabs', '/lib/ckeditor/plugins/bootstrapTabs/');
        }
    }, []);

    const handleChange = useCallback((event) => {
        const data = event.editor.getData();
        onChange(data);
    }, [onChange]);

    const handleInstanceReady = useCallback((event) => {
        const { editor } = event;
        setEditorInstance(editor);
        if (value) {
            editor.setData(value);
        }
    }, [value]);

    useEffect(() => {
        if (editorInstance && value !== undefined && value !== null) {
            const currentData = editorInstance.getData();
            if (currentData !== value) {
                editorInstance.setData(value);
            }
        }
    }, [value, editorInstance]);

    const config = short ? ShortConfig : EditorConfig;
    const finalConfig = {
        ...config,
        bodyClass: isDarkMode ? 'cke_editable dark-mode' : 'cke_editable',
    };

    return (
        <Labeled {...props} {...inputWithRequire}>
            <>
                <CKEditor
                    key={isDarkMode ? 'dark' : 'light'}
                    config={finalConfig}
                    initData={value || defValue}
                    name={name}
                    onChange={handleChange}
                    onBlur={onBlur}
                    onFocus={onFocus}
                    onBeforeLoad={handleBeforeLoad}
                    onInstanceReady={handleInstanceReady}
                />
                <FormHelperText
                    error={Boolean(error)}
                >
                    <InputHelperText
                        error={error}
                        touched={touched}
                    />
                </FormHelperText>
            </>
        </Labeled>
    );
};

RichTextInput.propTypes = {
    fullWidth: PropTypes.bool,
    helperText: PropTypes.string,
    label: PropTypes.string,
    defValue: PropTypes.string,
    margin: PropTypes.string,
    source: PropTypes.string.isRequired,
    resource: PropTypes.string,
    required: PropTypes.bool,
    short: PropTypes.bool,
};

export default React.memo(RichTextInput);
